import * as THREE from "three";
import type { FireResult, Weapon, WeaponHudState } from "./weapon";
import { hitscan } from "./ballistics";
import { bus } from "../core/bus";
import { Audio } from "../sfx/audio";
import type { WeaponMods } from "../meta/loadout";

const BASE_ROUNDS_PER_SEC = 15;
const BASE_SPREAD_RAD = 0.006;
const OVERHEAT_DURATION = 5;
const COOL_TIME = 3;

export class Chaingun implements Weapon {
  readonly id = "chaingun";
  readonly label = "25MM GAU-12";
  ammo = Number.POSITIVE_INFINITY;
  readonly maxAmmo = Number.POSITIVE_INFINITY;
  readonly infiniteAmmo = true;

  private cooldownLeft = 0;
  private readonly cooldown: number;
  private readonly spread: number;
  private heat = 0;
  private lockoutLeft = 0;

  constructor(mods?: Pick<WeaponMods, "cooldownMult" | "spreadMult">) {
    this.cooldown = (1 / BASE_ROUNDS_PER_SEC) * (mods?.cooldownMult ?? 1);
    this.spread = BASE_SPREAD_RAD * (mods?.spreadMult ?? 1);
  }

  get isReady(): boolean {
    return this.cooldownLeft <= 0 && this.lockoutLeft <= 0;
  }

  getHudState(): WeaponHudState {
    const cooling = this.lockoutLeft > 0;
    const heatPct = Math.round(this.heat * 100);
    return {
      weaponId: "chaingun",
      modeLabel: cooling ? "COOLING" : "HEAT",
      primaryLabel: cooling ? "BARREL RECOVERY" : "THERMAL LOAD",
      primaryValue: cooling ? 1 - this.lockoutLeft / COOL_TIME : this.heat,
      primaryText: cooling ? `${this.lockoutLeft.toFixed(1)}S` : `${heatPct}%`,
      secondaryLabel: "LIMITER",
      secondaryText: "5.0S BURST / 3.0S COOL",
      statusText: cooling ? "OVERHEATED" : this.heat >= 0.75 ? "HOT" : "ONLINE",
      shortText: cooling ? `COOL ${this.lockoutLeft.toFixed(1)}S` : `HEAT ${heatPct}%`,
      zoomText: "TIGHT",
    };
  }

  update(dt: number): void {
    if (this.cooldownLeft > 0) this.cooldownLeft = Math.max(0, this.cooldownLeft - dt);
    if (this.lockoutLeft > 0) {
      this.lockoutLeft = Math.max(0, this.lockoutLeft - dt);
      this.heat = this.lockoutLeft / COOL_TIME;
    } else if (this.cooldownLeft <= 0) {
      this.heat = Math.max(0, this.heat - dt / OVERHEAT_DURATION);
    }
  }

  fire(origin: THREE.Vector3, dir: THREE.Vector3, scene: THREE.Scene): FireResult {
    if (!this.isReady) return { fired: false, hitPoint: null, hitObject: null };

    this.cooldownLeft = this.cooldown;
    this.heat = Math.min(1, this.heat + this.cooldown / OVERHEAT_DURATION);
    if (this.heat >= 1) this.lockoutLeft = COOL_TIME;

    const result = hitscan(origin, dir, scene, this.spread);
    Audio.chaingunShot();
    bus.emit("weapon:fired", { id: this.id });

    return {
      fired: true,
      hitPoint: result?.point ?? null,
      hitObject: result?.object ?? null,
    };
  }
}
